import { Component } from '@theme/component';

/**
 * Horizontal carousel with scroll-snap and a cumulative progress indicator.
 *
 * @extends {Component<{ track: HTMLElement; progressTrack: HTMLElement; progressThumb: HTMLElement }>}
 */
class MultiColumnCarouselComponent extends Component {
  requiredRefs = ['track', 'progressTrack', 'progressThumb'];

  /** @type {(() => void) | undefined} */
  #onScroll;

  /** @type {(() => void) | undefined} */
  #onResize;

  /** @type {((event: Event) => void) | undefined} */
  #onNavClick;

  /** 
   * Observe the algolia recommendations root to update carousel when product cards are rendered
   * @type {MutationObserver} 
   */
  #algoliaMmutationObserver = new MutationObserver(() => {
    this.#scheduleThumbUpdate();
  });

  /** @type {number | undefined} */
  #thumbFrame;

  connectedCallback() {
    super.connectedCallback();

    this.#onScroll = () => this.#scheduleThumbUpdate();
    this.#onResize = () => this.#scheduleThumbUpdate();
    this.#onNavClick = (event) => this.#handleNavClick(event);
    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);

    for (const btn of this.querySelectorAll('[data-mcc-nav]')) {
      btn.addEventListener('click', this.#onNavClick);
    }

    if (this.algoliaRoot) {
      this.#algoliaMmutationObserver.observe(this.algoliaRoot, { childList: true, subtree: true });
    }

    this.#scheduleThumbUpdate();
  }

  /**
   * Read slide geometry on the next frame so layout is not forced during style setup.
   */
  #scheduleThumbUpdate() {
    if (this.#thumbFrame != null) return;

    this.#thumbFrame = requestAnimationFrame(() => {
      this.#thumbFrame = undefined;
      this.#updateThumbAndNav();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#thumbFrame != null) {
      cancelAnimationFrame(this.#thumbFrame);
      this.#thumbFrame = undefined;
    }

    if (this.#onScroll) this.refs.track?.removeEventListener('scroll', this.#onScroll);
    if (this.#onResize) window.removeEventListener('resize', this.#onResize);

    if (this.#onNavClick) {
      for (const btn of this.querySelectorAll('[data-mcc-nav]')) {
        btn.removeEventListener('click', this.#onNavClick);
      }
    }

    if (this.algoliaRoot) {
      this.#algoliaMmutationObserver.disconnect();
    }
  }

  get algoliaRoot() {
    return this.querySelector('.algolia-recommendations-root')
  }

  /**
   * @param {Event} event
   */
  #handleNavClick(event) {
    const target = event.currentTarget;
    if (!(target instanceof HTMLButtonElement)) return;
    if (target.disabled) return;

    const dir = target.dataset.mccNav === 'prev' ? -1 : 1;
    this.#scrollBySlide(dir);
  }

  /**
   * @param {number} direction -1 prev, +1 next
   */
  #scrollBySlide(direction) {
    const { track } = this.refs;
    const slide = track.querySelector('.mcc-slide');
    if (!slide) return;

    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    const delta = slide.getBoundingClientRect().width + gap;

    track.scrollBy({ left: direction * delta, behavior: 'smooth' });
  }

  #updateThumbAndNav() {
    const track = this.refs.track;
    const bar = this.refs.progressTrack;
    const thumb = this.refs.progressThumb;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const barW = bar.clientWidth;

    if (barW <= 0) return;

    const progress = maxScroll <= 0 ? 1 : Math.max(0, Math.min(1, track.scrollLeft / maxScroll));

    thumb.style.width = `${progress * 100}%`;
    thumb.style.left = '0px';
    thumb.setAttribute('aria-valuenow', String(Math.round(progress * 100)));

    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;
    const noScroll = maxScroll <= 0;

    for (const btn of this.querySelectorAll('[data-mcc-nav="prev"]')) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.disabled = noScroll || atStart;
    }
    for (const btn of this.querySelectorAll('[data-mcc-nav="next"]')) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.disabled = noScroll || atEnd;
    }
  }
}

if (!customElements.get('multi-column-carousel-component')) {
  customElements.define('multi-column-carousel-component', MultiColumnCarouselComponent);
}
